import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { queryAssignedElements } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { CommonFormElementSize } from '@/common/types'
import DapDSCheckbox from '@/components/checkbox/checkbox.component'
import DapDSSwitch from '@/components/switch/switch.component'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'

import DapDSFeedback from '../../feedback/feedback.component'
import DapDSIcon from '../../icon/icon.component'
import DapDSIconButton from '../../icon-button/icon-button.component'
import DapDSTooltip from '../../tooltip/tooltip.component'
import DapDSTypography from '../../typography/typography.component'
import DapDSFormLabel from '../form-label/form-label.component'
import styles from './input-group.scss?inline'

/**
 * `dap-ds-input-group`
 * @summary An input group is a container for inputs.
 * @element dap-ds-input-group
 * @title - Input group
 * @group form
 *
 * @property {string} label - The label of the input group.
 * @property {boolean} hideLabel - Visually hides the label while keeping it available to assistive technology. (default: false)
 * @property {string} description - The description of the input group.
 * @property {string} tooltip - The tooltip of the input group.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the input group.
 * @property {string} tooltipAriaLabel - The aria label of the tooltip.
 * @property {string} optionalLabel - The label of the input group when it is optional.
 * @property {string} requiredLabel - The indicator of the input group when it is required. (default: '*')
 * @property {boolean} disabled - Whether the input group is disabled.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the input group. Default is `sm`.
 * @property {boolean} required - Whether the input group is required.
 * @property {boolean} optional - Whether the input group is optional.
 * @property {string} feedback - The feedback of the input.
 * @property {string} feedbackType - The type of the feedback.
 * @property {boolean} subtle - Whether the input group label is subtle.
 *
 * @slot - The content of the input group items.
 * @slot feedback-icon - The custom icon of the feedback.
 *
 * @csspart base - The main input group container.
 * @csspart label - The label of the input group.
 * @csspart description - The description of the input group.
 * @csspart tooltip - The tooltip of the input group.
 * @csspart container - The container of the input group items.
 */
export default class DapDSInputGroup extends GenericFormElement {
  static tagName = 'dap-ds-input-group'

  static dependencies = {
    'dap-ds-form-label': DapDSFormLabel,
    'dap-ds-tooltip': DapDSTooltip,
    'dap-ds-icon-button': DapDSIconButton,
    'dap-ds-icon': DapDSIcon,
    'dap-ds-typography': DapDSTypography,
    'dap-ds-feedback': DapDSFeedback,
  }

  static readonly styles = unsafeCSS(styles)

  @queryAssignedElements() allInputs: DapDSCheckbox[] | DapDSSwitch[]

  handleLabelClick() {
    const checkedInput =
      this.allInputs.find(input => input.checked) ?? this.allInputs[0]

    checkedInput?.focus()
  }

  private getItems() {
    return [
      ...this.querySelectorAll<DapDSSwitch | DapDSCheckbox>(
        'dap-ds-switch, dap-ds-checkbox',
      ),
    ]
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ) {
    super.attributeChangedCallback(name, _old, value)
    if (name === 'disabled' || name === 'invalid') {
      this.getItems().forEach(item => {
        item[name] = value !== null
      })
    }
  }

  render() {
    return html`
      <fieldset
        id="input"
        class="input-group"
        part="base"
        .disabled=${this.disabled}
        aria-labelledby="label"
        aria-describedby=${`description ${this.feedbackId}`}
        aria-errormessage=${ifDefined(this.feedbackId)}>
        ${(this.label || this.tooltip) &&
        html`<legend class="input-group__legend">
          <div class="label-container">
            ${this.label &&
            html`<dap-ds-form-label
              part="label"
              id="label"
              .size=${this.size}
              .required=${this.required}
              .requiredLabel=${this.requiredLabel}
              .optional=${this.optional}
              .optionalLabel=${this.optionalLabel}
              .disabled=${this.disabled}
              .subtle=${this.subtle}
              .noMargin=${!!this.description}
              .hideLabel=${this.hideLabel}
              label=${ifDefined(this.label)}
              @click=${this.handleLabelClick}>
            </dap-ds-form-label>`}
            ${this.tooltip &&
            html` <dap-ds-tooltip
              part="tooltip"
              content=${this.tooltip}
              mode="toggle"
              placement=${this.tooltipPlacement}>
              <dap-ds-icon-button
                clean
                class="input-group__toggle-button"
                slot="trigger"
                aria-label=${this.tooltipAriaLabel}
                size=${this.size as 'xs' | 'sm' | 'lg'}
                iconSize=${this.size === 'xs' ? '16' : '22'}>
                <dap-ds-icon
                  name="information-line"
                  class="info-button"></dap-ds-icon>
              </dap-ds-icon-button>
            </dap-ds-tooltip>`}
          </div>
          ${this.description &&
          html`
            <dap-ds-typography
              part="description"
              variant="description"
              class=${clsx('input-description')}
              size=${ifDefined(this.size as CommonFormElementSize)}
              id="description">
              ${this.description}
            </dap-ds-typography>
          `}
        </legend>`}
        <div part="container" class="input-group__items">
          <slot></slot>
        </div>
        ${this.feedback && this._renderFeedback()}
      </fieldset>
    `
  }

  private _handleSlotChange(event: Event) {
    const target = event?.target as HTMLInputElement
    target.setAttribute('slot', 'icon')
  }

  private _renderFeedback() {
    return html`
      <dap-ds-feedback
        part="feedback"
        feedbackType=${ifDefined(this.feedbackType)}
        feedbackSize="${ifDefined(this.size as 'lg' | 'sm' | 'xs')}"
        id="feedback"
        .subtle=${this.subtle}>
        <slot
          name="feedback-icon"
          @slotchange="${this._handleSlotChange}"></slot>
        ${this.feedback}
      </dap-ds-feedback>
    `
  }
}
